import * as Sentry from "@sentry/react";

/**
 * Request id — one UUID per API call, sent as `X-Request-Id`.
 *
 * The backend stamps it on every CloudWatch log line of that request as
 * `correlation_id` (and on its Sentry events), so a failed call in the browser
 * can be traced to the exact backend log lines. A 401 refresh-and-retry reuses
 * the same id, so both attempts appear under one correlation id.
 *
 * Unlike `X-Client-Session-Id` (one per tab), this is fresh for every call.
 */
export const REQUEST_ID_HEADER = "X-Request-Id";

export function createRequestId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `rid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Leave a Sentry trail for a failed API call: a breadcrumb on the current
 * scope plus a `last_failed_request_id` tag, so any error Sentry captures
 * afterwards links straight to the backend logs for that call. The query
 * string is dropped from the endpoint because it can carry search terms.
 */
export function recordFailedRequest(
  requestId: string,
  status: number,
  endpoint: string,
): void {
  Sentry.addBreadcrumb({
    category: "api",
    level: "error",
    message: `${status} ${endpoint.split("?")[0]}`,
    data: { requestId },
  });
  Sentry.setTag("last_failed_request_id", requestId);
}
